import React, { useEffect, useState } from 'react';
import { X, Download, BellRing } from 'lucide-react';
import toast from 'react-hot-toast';
import { isMobile, isStandalone, comoInstalar, canPromptInstall, onInstallAvailable, promptInstall, pushSupported, permission, enablePush, isPushActiveHere } from '../utils/pwa';

const DISMISS_DAYS = 7;
const dismissed = (key) => { try { const t = parseInt(localStorage.getItem(key), 10); return t && Date.now() - t < DISMISS_DAYS * 86400000; } catch { return false; } };
const dismiss = (key) => { try { localStorage.setItem(key, String(Date.now())); } catch {} };

// En celular: sugiere instalar la app en el inicio. Ya instalada: sugiere activar las notificaciones.
export default function InstallPrompt() {
  const [mode, setMode] = useState(null); // 'install' | 'push' | null
  const [canInstall, setCanInstall] = useState(canPromptInstall());

  useEffect(() => onInstallAvailable(setCanInstall), []);
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!isMobile()) return;
      if (!isStandalone()) { if (!dismissed('pwa_install_dismissed')) setMode('install'); return; }
      if (pushSupported() && permission() === 'default' && !dismissed('pwa_push_dismissed') && !(await isPushActiveHere().catch(() => false))) setMode('push');
    }, 2500);
    return () => clearTimeout(t);
  }, []);

  if (!mode) return null;
  const close = () => { dismiss(mode === 'install' ? 'pwa_install_dismissed' : 'pwa_push_dismissed'); setMode(null); };

  const install = async () => {
    const ok = await promptInstall();
    if (ok) { toast.success('¡Listo! Abrí Udream App desde el ícono de tu inicio y activá las notificaciones.', { duration: 7000 }); setMode(null); }
  };
  const activate = async () => {
    const r = await enablePush().catch(() => 'error');
    if (r === 'granted') toast.success('Notificaciones activadas');
    else if (r === 'denied') toast.error('No se activaron. Podés habilitarlas desde la configuración del celular.', { duration: 7000 });
    dismiss('pwa_push_dismissed'); setMode(null);
  };

  return (
    <div className="pwa-banner">
      <img src="/icon-192.png?v=2" alt="" style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {mode === 'install' ? (<>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Instalá Udream App en tu celular</div>
          {canInstall ? (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Queda con acceso directo en tu inicio y te avisa de nuevos leads y mensajes.</div>
          ) : (() => {
            /* Los pasos del navegador en el que está la persona, no los de Safari
               siempre. Nombrarlo sirve además como señal de que la detección
               acertó: si dice "Chrome" y está en Chrome, le creés al resto. */
            const { nombre, pasos, nota } = comoInstalar();
            return (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.5 }}>
                {nombre ? <>En <b>{nombre}</b> tocá </> : 'Abrí '}
                {pasos.map((p, i) => (
                  <React.Fragment key={p}>
                    {i > 0 && ' → '}<b>{p}</b>
                  </React.Fragment>
                ))}.
                {' '}Abrila desde ese ícono para activar las notificaciones.
                {nota && <div style={{ color: 'var(--text-muted)', marginTop: 3 }}>{nota}</div>}
              </div>
            );
          })()}
          {canInstall && <button className="btn btn-primary btn-sm" onClick={install} style={{ marginTop: 8 }}><Download size={14} /> Instalar</button>}
        </>) : (<>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Activá las notificaciones</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.45 }}>Enterate al instante de nuevos leads y mensajes. Cuando el celular pregunte, tocá <b>"Permitir"</b>.</div>
          <button className="btn btn-primary btn-sm" onClick={activate} style={{ marginTop: 8 }}><BellRing size={14} /> Activar notificaciones</button>
        </>)}
      </div>
      <button className="btn btn-ghost btn-sm" onClick={close} style={{ padding: 4, alignSelf: 'flex-start' }} aria-label="Cerrar"><X size={16} /></button>
    </div>
  );
}
